import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet
} from "@react-pdf/renderer";


const formatCurrency = (
    amount,
    currency = "LRD"
) => {
    const value = Number(
        amount || 0
    );

    if (currency === "USD") {
        return `$${value.toLocaleString(
            "en-US",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        )}`;
    }

    return `LD ${value.toLocaleString(
        "en-US",
        {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }
    )}`;
};


const formatDate = (
    value
) => {
    if (!value) {
        return "All Dates";
    }

    const date = new Date(
        value
    );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return value;
    }

    return date.toLocaleDateString(
        "en-US",
        {
            year: "numeric",
            month: "long",
            day: "numeric"
        }
    );
};


const styles = StyleSheet.create({
    page: {
        paddingTop: 34,
        paddingBottom: 48,
        paddingHorizontal: 34,
        backgroundColor: "#ffffff",
        fontSize: 9,
        color: "#111827"
    },

    header: {
        borderBottomWidth: 2,
        borderBottomColor: "#111827",
        paddingBottom: 14,
        marginBottom: 18
    },

    logo: {
        width: 50,
        height: 50,
        objectFit: "contain",
        alignSelf: "center",
        marginBottom: 7
    },

    schoolName: {
        fontSize: 20,
        fontWeight: 900,
        textAlign: "center",
        color: "#111827"
    },

    location: {
        fontSize: 9,
        textAlign: "center",
        color: "#6b7280",
        marginTop: 4
    },

    reportTitle: {
        fontSize: 15,
        fontWeight: 900,
        textAlign: "center",
        color: "#f97316",
        marginTop: 10
    },

    academicYearBadge: {
        marginTop: 7,
        alignSelf: "center",
        backgroundColor: "#fff7ed",
        borderRadius: 4,
        paddingVertical: 5,
        paddingHorizontal: 10
    },

    academicYearText: {
        fontSize: 8,
        fontWeight: 700,
        color: "#c2410c",
        textAlign: "center"
    },

    infoBox: {
        backgroundColor: "#f9fafb",
        borderWidth: 1,
        borderColor: "#e5e7eb",
        borderRadius: 6,
        padding: 10,
        marginBottom: 18
    },

    infoRow: {
        flexDirection: "row",
        flexWrap: "wrap"
    },

    infoItem: {
        width: "50%",
        marginBottom: 7
    },

    infoLabel: {
        fontSize: 8,
        fontWeight: 800,
        color: "#374151"
    },

    infoValue: {
        fontSize: 8,
        color: "#111827",
        marginTop: 3
    },

    sectionTitle: {
        fontSize: 11,
        fontWeight: 900,
        color: "#111827",
        marginBottom: 8
    },

    table: {
        borderWidth: 1,
        borderColor: "#d1d5db",
        borderRadius: 5,
        overflow: "hidden"
    },

    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#111827",
        paddingVertical: 9,
        paddingHorizontal: 9
    },

    tableRow: {
        flexDirection: "row",
        paddingVertical: 10,
        paddingHorizontal: 9,
        borderBottomWidth: 1,
        borderBottomColor: "#e5e7eb"
    },

    tableRowLast: {
        flexDirection: "row",
        paddingVertical: 10,
        paddingHorizontal: 9
    },

    categoryColumn: {
        width: "42%"
    },

    amountColumn: {
        width: "29%",
        textAlign: "right"
    },

    tableHeaderText: {
        color: "#ffffff",
        fontSize: 9,
        fontWeight: 800
    },

    categoryText: {
        fontSize: 9,
        fontWeight: 700,
        color: "#111827"
    },

    amountText: {
        fontSize: 9,
        color: "#111827",
        textAlign: "right"
    },

    totalCard: {
        marginTop: 20,
        backgroundColor: "#e7eaef",
        borderRadius: 8,
        padding: 15
    },

    totalTitle: {
        color: "#000000",
        fontSize: 12,
        fontWeight: 900,
        textAlign: "center",
        marginBottom: 10
    },

    totalRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingTop: 9,
        marginTop: 7,
        borderTopWidth: 1,
        borderTopColor: "#374151"
    },

    totalLabel: {
        fontSize: 10,
        color: "#000000",
        fontWeight: 700
    },

    totalValue: {
        fontSize: 11,
        color: "#030303",
        fontWeight: 900
    },

    noteBox: {
        marginTop: 16,
        backgroundColor: "#fff7ed",
        borderLeftWidth: 3,
        borderLeftColor: "#f97316",
        padding: 9
    },

    noteText: {
        fontSize: 8,
        color: "#78350f",
        lineHeight: 1.5
    },

    footer: {
        position: "absolute",
        bottom: 18,
        left: 34,
        right: 34,
        borderTopWidth: 1,
        borderTopColor: "#d1d5db",
        paddingTop: 7,
        flexDirection: "row",
        justifyContent: "space-between"
    },

    footerText: {
        fontSize: 7,
        color: "#6b7280"
    }
});


const IncomeStatementPDF = ({
    incomeStatement,
    selectedYear,
    semester,
    startDate,
    endDate,
    schoolName = "Excellent Kids Foundation",
    schoolLocation = "GreenLand Commnity, Upper Johnsonville TownShip - Left Bank 2B, Montserrade County, Liberia",
    schoolLogo = null
}) => {
    const statement = (
        incomeStatement?.income_statement
        || incomeStatement
        || {}
    );

    const tuition = (
        statement.tuition
        || {}
    );

    const scholarship = (
        statement.scholarship
        || {}
    );

    const ward = (
        statement.ward
        || {}
    );

    const totalIntake = (
        statement.total_intake
        || {}
    );

    const generatedDate = new Date()
        .toLocaleString(
            "en-US",
            {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            }
        );

    const academicYearName = (
        selectedYear?.name
        || selectedYear?.academic_year
        || "Academic Year Not Selected"
    );


    return (
        <Document
            title="Income Statement"
            author="Polu Integrated School Management System"
        >
            <Page
                size="A4"
                style={styles.page}
            >
                {/* HEADER */}

                <View
                    style={styles.header}
                >
                    {
                        schoolLogo ? (
                            <Image
                                src={schoolLogo}
                                style={styles.logo}
                            />
                        ) : null
                    }

                    <Text
                        style={styles.schoolName}
                    >
                        {schoolName}
                    </Text>

                    <Text
                        style={styles.location}
                    >
                        {schoolLocation}
                    </Text>

                    <Text
                        style={styles.reportTitle}
                    >
                        INCOME STATEMENT
                    </Text>

                    <View
                        style={styles.academicYearBadge}
                    >
                        <Text
                            style={styles.academicYearText}
                        >
                            Academic Year: {academicYearName}
                        </Text>
                    </View>
                </View>


                {/* REPORT INFORMATION */}

                <View
                    style={styles.infoBox}
                >
                    <View
                        style={styles.infoRow}
                    >
                        <View
                            style={styles.infoItem}
                        >
                            <Text
                                style={styles.infoLabel}
                            >
                                Academic Year
                            </Text>

                            <Text
                                style={styles.infoValue}
                            >
                                {academicYearName}
                            </Text>
                        </View>


                        <View
                            style={styles.infoItem}
                        >
                            <Text
                                style={styles.infoLabel}
                            >
                                Semester
                            </Text>

                            <Text
                                style={styles.infoValue}
                            >
                                {
                                    semester
                                    || "All Semesters"
                                }
                            </Text>
                        </View>


                        <View
                            style={styles.infoItem}
                        >
                            <Text
                                style={styles.infoLabel}
                            >
                                Start Date
                            </Text>

                            <Text
                                style={styles.infoValue}
                            >
                                {
                                    formatDate(
                                        startDate
                                    )
                                }
                            </Text>
                        </View>


                        <View
                            style={styles.infoItem}
                        >
                            <Text
                                style={styles.infoLabel}
                            >
                                End Date
                            </Text>

                            <Text
                                style={styles.infoValue}
                            >
                                {
                                    formatDate(
                                        endDate
                                    )
                                }
                            </Text>
                        </View>
                    </View>
                </View>


                {/* INCOME BREAKDOWN */}

                <Text
                    style={styles.sectionTitle}
                >
                    Income Breakdown
                </Text>


                <View
                    style={styles.table}
                >
                    <View
                        style={styles.tableHeader}
                    >
                        <Text
                            style={[
                                styles.categoryColumn,
                                styles.tableHeaderText
                            ]}
                        >
                            Income Category
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.tableHeaderText
                            ]}
                        >
                            LRD
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.tableHeaderText
                            ]}
                        >
                            USD
                        </Text>
                    </View>


                    <View
                        style={styles.tableRow}
                    >
                        <Text
                            style={[
                                styles.categoryColumn,
                                styles.categoryText
                            ]}
                        >
                            Tuition (Old + New Students)
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.amountText
                            ]}
                        >
                            {
                                formatCurrency(
                                    tuition.lrd,
                                    "LRD"
                                )
                            }
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.amountText
                            ]}
                        >
                            {
                                formatCurrency(
                                    tuition.usd,
                                    "USD"
                                )
                            }
                        </Text>
                    </View>


                    <View
                        style={styles.tableRow}
                    >
                        <Text
                            style={[
                                styles.categoryColumn,
                                styles.categoryText
                            ]}
                        >
                            Scholarship
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.amountText
                            ]}
                        >
                            {
                                formatCurrency(
                                    scholarship.lrd,
                                    "LRD"
                                )
                            }
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.amountText
                            ]}
                        >
                            {
                                formatCurrency(
                                    scholarship.usd,
                                    "USD"
                                )
                            }
                        </Text>
                    </View>


                    <View
                        style={styles.tableRowLast}
                    >
                        <Text
                            style={[
                                styles.categoryColumn,
                                styles.categoryText
                            ]}
                        >
                            Ward
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.amountText
                            ]}
                        >
                            {
                                formatCurrency(
                                    ward.lrd,
                                    "LRD"
                                )
                            }
                        </Text>

                        <Text
                            style={[
                                styles.amountColumn,
                                styles.amountText
                            ]}
                        >
                            {
                                formatCurrency(
                                    ward.usd,
                                    "USD"
                                )
                            }
                        </Text>
                    </View>
                </View>


                {/* TOTAL INTAKE */}

                <View
                    style={styles.totalCard}
                >
                    <Text
                        style={styles.totalTitle}
                    >
                        TOTAL INTAKE
                    </Text>

                    <View
                        style={styles.totalRow}
                    >
                        <Text
                            style={styles.totalLabel}
                        >
                            Total Intake — LRD
                        </Text>

                        <Text
                            style={styles.totalValue}
                        >
                            {
                                formatCurrency(
                                    totalIntake.lrd,
                                    "LRD"
                                )
                            }
                        </Text>
                    </View>


                    <View
                        style={styles.totalRow}
                    >
                        <Text
                            style={styles.totalLabel}
                        >
                            Total Intake — USD
                        </Text>

                        <Text
                            style={styles.totalValue}
                        >
                            {
                                formatCurrency(
                                    totalIntake.usd,
                                    "USD"
                                )
                            }
                        </Text>
                    </View>
                </View>


                {/* REPORT NOTE */}

                <View
                    style={styles.noteBox}
                >
                    <Text
                        style={styles.noteText}
                    >
                        This Income Statement represents
                        payments received under the selected
                        Academic Year. Tuition, Scholarship,
                        and Ward payments are calculated
                        separately, while LRD and USD are
                        maintained as separate currencies.
                    </Text>
                </View>


                {/* FOOTER */}

                <View
                    style={styles.footer}
                    fixed
                >
                    <Text
                        style={styles.footerText}
                    >
                        Generated: {generatedDate}
                    </Text>

                    <Text
                        style={styles.footerText}
                    >
                        Generated / Designed by:
                        Polu Integrated School Management System
                    </Text>
                </View>
            </Page>
        </Document>
    );
};
export default IncomeStatementPDF;
